import { action, makeObservable, observable } from 'mobx'

import { LocationType } from '~/client/graph'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import {
  getLocationKind,
  LOCATION_KIND_PROPERTY,
} from '~/client/src/shared/resources/domain/locationKind'
import InitialState from '~/client/src/shared/stores/InitialState'
import IHierarchyParent from '~/client/src/shared/types/IHierarchyParent'
import ILocationDto from '~/client/src/shared/types/ILocationDto'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import Guard from '~/client/src/shared/utils/Guard'
import { DEFAULT_SITEMAP_ITEM_COLOR } from '~/client/src/shared/utils/SitemapItemsColors'

import FieldIds from '../../enums/DeliveryFieldIds'
import CompaniesStore from '../../stores/domain/Companies.store'
import {
  areArraysEqual,
  areObjectArraysEqual,
  areObjectsEqual,
  copyObject,
  copyObjectArray,
} from '../../utils/util'
import { LocationIntegrationType } from './LocationIntegration'

export default abstract class LocationBase<
  T extends LocationBase<T> = any,
> implements ILocationDto {
  public static factory<U>(
    model: new (...args: any) => U,
    dto: ILocationDto,
    defaultIcon: MapViewLocationIcon,
  ): U {
    const instance = new model(
      dto.id,
      dto.name,
      dto.color || DEFAULT_SITEMAP_ITEM_COLOR,
      dto.iconName || defaultIcon,
      dto.projectId,
      dto.code,
      Object.values(dto.operatingIntervals || {}),
      dto.parent || { parentId: dto.projectId },
      Object.values(dto.permittedCompanies || {}),
      dto.isDeleted,
      Object.values(dto.assignedSitemaps || {}),
      Object.values(dto.assignedGlobes || {}),
    )
    // showInMap lives outside the positional constructor; overlay it when the DTO
    // provides it, otherwise keep the field's default (true).
    if (typeof dto.showInMap === 'boolean') {
      ;(instance as unknown as LocationBase).showInMap = dto.showInMap
    }
    if (typeof dto.isBookable === 'boolean') {
      ;(instance as unknown as LocationBase).isBookable = dto.isBookable
    }
    // templateId and data likewise. They are overlaid rather than added to the
    // positional constructor deliberately: LocationBaseWithDimensions already
    // declares twelve parameters while copy() calls it with fifteen, so a new
    // positional argument is how the next silent drop gets introduced.
    ;(instance as unknown as LocationBase).templateId = dto.templateId ?? null
    ;(instance as unknown as LocationBase).data = dto.data ?? null
    ;(instance as unknown as LocationBase).stxResourceId =
      dto.stxResourceId || null
    return instance
  }

  public static generateCodeFromName(name: string) {
    return name
      .split(' ')
      .map(part => part.slice(0, 3))
      .filter(l => l)
      .slice(0, 3)
      .join('')
      .toUpperCase()
  }

  @observable public id: string = null
  @observable public name: string = null
  @observable public code: string = null
  @observable public stxResourceId: string | null = null
  @observable public color: string = null
  @observable public iconName: MapViewLocationIcon = null
  public projectId: string
  @observable public operatingIntervals: IMsDateInterval[] = []
  @observable public permittedCompanies: string[] = []
  @observable public parent: IHierarchyParent = null
  @observable public isDeleted: boolean = false
  @observable public assignedSitemaps: string[] = []
  @observable public assignedGlobes: string[] = []
  // Independent display flag (default true). Set from the DTO in `factory`; not a
  // constructor param and intentionally excluded from getDto/isEqual — it is
  // persisted only via the dedicated updateLocationsShowInMap controls mutation.
  @observable public showInMap: boolean = true
  // Like showInMap: outside the positional constructor and out of getDto/isEqual;
  // persisted only via the resource controls mutation.
  @observable public isBookable: boolean = true

  // The resource fields. Like showInMap they sit outside the positional constructor
  // and are overlaid in factory(); every location carries them now that a location is
  // a resource, and data is where its kind lives (STX1-597).
  @observable public templateId?: string | null = null
  @observable public data?: Record<string, any> | null = null

  public abstract type: LocationType
  protected abstract relatedFieldId: FieldIds
  protected abstract model: new (...args: any) => T

  public constructor(
    id: string,
    name: string,
    color: string,
    iconName: MapViewLocationIcon,
    projectId: string,
    code?: string,
    operatingIntervals: IMsDateInterval[] = [],
    parent?: IHierarchyParent,
    permittedCompanies: string[] = [],
    isDeleted: boolean = false,
    assignedSitemaps: string[] = [],
    assignedGlobes: string[] = [],
  ) {
    Guard.requireAll({ name, color, projectId, iconName })
    this.id = id
    this.name = name
    this.code = code || LocationBase.generateCodeFromName(name)
    this.color = color
    this.iconName = iconName
    this.projectId = projectId
    this.operatingIntervals = operatingIntervals
    this.parent = parent
    this.permittedCompanies = permittedCompanies
    this.isDeleted = isDeleted
    this.assignedSitemaps = assignedSitemaps
    this.assignedGlobes = assignedGlobes
    makeObservable(this)
  }

  public is(type: LocationType | LocationIntegrationType): boolean {
    return this.type === type
  }

  public get hasParent(): boolean {
    return !!this.parent
  }

  public get hasAnyCompanyRestriction(): boolean {
    return !!this.permittedCompanies.length
  }

  public getAllPermittedCompaniesNames = (
    companiesStore: CompaniesStore,
  ): string[] =>
    this.permittedCompanies.map(companyId =>
      companiesStore.getCompanyNameById(companyId),
    ) || []

  public hasRestrictionForCompany = (companyId: string): boolean => {
    return this.hasRestrictionForCompanies([companyId])
  }

  public hasRestrictionForCompanies = (companyIds: string[]): boolean => {
    if (!this.hasAnyCompanyRestriction) {
      return false
    }

    return (
      !companyIds?.length ||
      companyIds.some(id => !this.permittedCompanies.includes(id))
    )
  }

  // By id alone. An id names one document whatever its type, and parentType is no longer a
  // reliable second key: a location reparented or re-kinded server-side can carry a
  // parentType ("Location", or the parent's old kind) that differs from the parent's model.
  public isParent(dto: LocationBase): boolean {
    return !!dto && !!this.parent && this.parent.parentId === dto.id
  }

  public hasCommonParent(dto: LocationBase): boolean {
    return (
      !!dto &&
      !!dto.parent &&
      !!this.parent &&
      this.parent.parentId === dto.parent.parentId
    )
  }

  public isGlobeAssigned(globeId: string) {
    return this.assignedGlobes?.includes(globeId)
  }

  @action
  public assignGlobe(globeId: string) {
    if (!this.isGlobeAssigned(globeId)) {
      this.assignedGlobes.push(globeId)
    }
  }

  @action
  public deassignGlobe(globeId: string) {
    if (this.isGlobeAssigned(globeId)) {
      this.assignedGlobes = this.assignedGlobes.filter(id => id !== globeId)
    }
  }

  public isSitemapAssigned(sitemapId: string) {
    return this.assignedSitemaps && this.assignedSitemaps.includes(sitemapId)
  }

  public assignSitemap(sitemapId: string) {
    if (!this.isSitemapAssigned(sitemapId)) {
      this.assignedSitemaps.push(sitemapId)
    }
  }

  public deassignSitemap(sitemapId: string) {
    if (this.isSitemapAssigned(sitemapId)) {
      this.assignedSitemaps = this.assignedSitemaps.filter(
        id => id !== sitemapId,
      )
    }
  }

  public isEqual(dto: T) {
    return (
      this.id === dto.id &&
      this.name === dto.name &&
      this.code === dto.code &&
      this.iconName === dto.iconName &&
      this.color === dto.color &&
      this.projectId === dto.projectId &&
      areObjectArraysEqual(
        this.operatingIntervals || [],
        dto.operatingIntervals || [],
      ) &&
      areObjectsEqual(this.parent || {}, dto.parent || {}) &&
      areArraysEqual(this.permittedCompanies, dto.permittedCompanies) &&
      this.isDeleted === dto.isDeleted &&
      this.stxResourceId === (dto.stxResourceId || null) &&
      areArraysEqual(this.assignedSitemaps || [], dto.assignedSitemaps || []) &&
      areArraysEqual(this.assignedGlobes || [], dto.assignedGlobes || [])
    )
  }

  public copy(): T {
    const item = new this.model(
      this.id,
      this.name,
      this.color,
      this.iconName,
      this.projectId,
      this.code,
      copyObjectArray(this.operatingIntervals),
      this.parent && copyObject(this.parent),
      this.permittedCompanies,
      this.isDeleted,
      this.assignedSitemaps,
      this.assignedGlobes,
    )
    // Not a constructor param — carry it over so copies (used by map renderers)
    // keep the hidden state.
    ;(item as unknown as LocationBase).showInMap = this.showInMap
    ;(item as unknown as LocationBase).isBookable = this.isBookable
    // Not constructor params — carry them over, the same as showInMap. A field
    // added outside the positional constructor has to be handled in BOTH factory()
    // and copy(); missing copy() is invisible until something copies before saving,
    // and the map save does exactly that (selectedMapViewItem.copy()), which silently
    // dropped the kind and re-derived the root from the legacy enum.
    ;(item as unknown as LocationBase).templateId = this.templateId ?? null
    ;(item as unknown as LocationBase).data = this.data
      ? { ...this.data }
      : null
    ;(item as unknown as LocationBase).stxResourceId = this.stxResourceId
    return item
  }

  /**
   * The model's `data` with its kind asserted into it.
   *
   * Null when there is neither, so the save handler's `request.Data ??= existing.Data`
   * preserves what is stored rather than clearing it.
   */
  private dataWithKind(): Record<string, any> | null {
    const kind = getLocationKind({ type: this.type, data: this.data })
    if (!kind) return this.data || null

    return { ...(this.data || {}), [LOCATION_KIND_PROPERTY]: kind }
  }

  public getDto(): ILocationDto {
    return {
      id: this.id,
      name: this.name,
      code: this.code,
      color: this.color,
      iconName: this.iconName,
      projectId: this.projectId,
      // isDaily and dayOfWeek must both round-trip. Dropping isDaily leaves the
      // Daily/Date mode to be inferred from the dates, which reads a same-day DATE
      // window back as a daily one. Dropping dayOfWeek is worse: a weekly schedule
      // comes back as several identical every-day windows, which collapse to one —
      // so the location reads as open every day, including the days it states no
      // hours for.
      operatingIntervals: this.operatingIntervals.map(i => ({
        startDate: i.startDate,
        endDate: i.endDate,
        isDaily: i.isDaily,
        dayOfWeek: i.dayOfWeek,
      })),
      parent:
        this.parent && this.parent.parentId !== this.projectId
          ? { parentId: this.parent.parentId }
          : null,
      permittedCompanies: this.permittedCompanies,
      assignedSitemaps: this.assignedSitemaps,
      assignedGlobes: this.assignedGlobes,
      // Every location is a resource, so every location serializes its resource
      // fields - not just equipment, which used to be the only class that did.
      // Without them a location saved through the map path lost its kind: `data`
      // never reached the server, so the row came back with no data.type and the
      // reader fell through to the legacy enum, turning a stairway back into a
      // vertical object.
      //
      // null rather than omitted when unset: the save handler does
      // `request.Data ??= existing.Data`, so null preserves what is stored while a
      // real object replaces it wholesale - which is what a fully-loaded model wants.
      templateId: this.templateId || null,
      // The kind travels in data.type, because saveManyLocations writes a plain
      // Location and the mutation name no longer says what kind of place this is.
      // Re-asserted from the model rather than trusted to already be in data: a
      // location drawn on the map has its kind in its class, not in its data.
      data: this.dataWithKind(),
    }
  }

  public getFieldName(state: InitialState): string {
    return state.getDeliveryFieldName(this.relatedFieldId)
  }

  public getHierarchyWithoutProject(tagStoreByTagTypeMap: any): string[] {
    const chain = this.retrieveHierarchyChains(
      this,
      [],
      tagStoreByTagTypeMap,
      true,
    )

    return chain.map(obj => obj.name)
  }

  public getHierarchyChains(tagStoreByTagTypeMap: any): string[] {
    const chain = this.getHierarchyChainObjs(tagStoreByTagTypeMap)

    return chain.map(obj => obj.name)
  }

  public getHierarchyChainsObjects(tagStoreByTagTypeMap: any): LocationBase[] {
    return this.getHierarchyChainObjs(tagStoreByTagTypeMap)
  }

  public getHierarchyChainObjs(tagStoreByTagTypeMap: any): LocationBase[] {
    return this.retrieveHierarchyChains(this, [], tagStoreByTagTypeMap)
  }

  private retrieveHierarchyChains(
    obj: LocationBase,
    path: LocationBase[],
    tagStoreByTagTypeMap: any,
    isProjectLocIgnored: boolean = false,
    visited: Set<string> = new Set(),
  ): LocationBase[] {
    if (!obj.hasParent) {
      return path
    }

    const { parentId } = obj.parent
    if (visited.has(parentId)) {
      return path
    }
    visited.add(parentId)

    const parentObj = findParentById(parentId, tagStoreByTagTypeMap)
    if (!parentObj) {
      return []
    }

    if (isProjectLocIgnored && parentObj.is(LocationType.ProjectLocation)) {
      return path
    }

    return this.retrieveHierarchyChains(
      parentObj,
      path.concat(parentObj),
      tagStoreByTagTypeMap,
      isProjectLocIgnored,
      visited,
    )
  }
}

const LOCATION_STORE_KEYS: ReadonlySet<string> = new Set(
  Object.values(LocationType),
)

/**
 * The parent location with this id, from whichever store holds it. A parent is named by id
 * alone - the client no longer selects parentType - and ids are unique across stores, so a
 * match by id is the parent.
 *
 * The location stores (keyed by a LocationType) are searched first. parentType used to send
 * the lookup straight to the right one; without it, a plain scan in map order would pass
 * through the people and role stores first - for every site-root and catch-all parent -
 * and their lookups rebuild tag lists. The rest are still tried, as before.
 */
function findParentById(
  parentId: string,
  tagStoreByTagTypeMap: any,
): LocationBase {
  const entries = Object.entries(tagStoreByTagTypeMap ?? {})
  const ordered = [
    ...entries.filter(([key]) => LOCATION_STORE_KEYS.has(key)),
    ...entries.filter(([key]) => !LOCATION_STORE_KEYS.has(key)),
  ]

  for (const [, store] of ordered) {
    const found = (store as any)?.getInstanceById?.(parentId)
    if (found instanceof LocationBase) {
      return found
    }
  }

  return null
}
